import React, { useEffect, useState, useContext } from "react";
import IconButton from "@material-ui/core/IconButton";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { Menu, MenuItem } from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import logo from "../../assets/logo.png";
import { toast } from 'react-toastify';
import toastError from '../../errors/toastError';
import 'react-toastify/dist/ReactToastify.css';
import {
  TranslateOutlined,
  WbSunnyOutlined,
  NightsStayOutlined,
} from "@material-ui/icons";
import LanguageControl from "../../components/LanguageControl";
import ColorModeContext from "../../layout/themeContext";

/**
 * ==== Estilos Floating UI ====
 * Cores sólidas, sombras profundas, elementos flutuantes
 * Sem glassmorphism, sem scroll
 */
const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";

  return {
    // Container principal - ocupa viewport inteira, sem scroll
    root: {
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      backgroundColor: isLight ? theme.palette.primary.main : "#1c1c1c",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    },

    // Card flutuante com sombras profundas
    paper: {
      backgroundColor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "48px 40px",
      borderRadius: theme.neoUI?.floating.card?.borderRadius || 20,
      boxShadow: theme.neoUI?.floating.card?.boxShadow || "0 20px 60px -10px rgba(0, 0, 0, 0.15)",
      border: theme.neoUI?.floating.card?.border || "1px solid rgba(0, 0, 0, 0.04)",
      transition: "transform 0.3s ease, box-shadow 0.3s ease",
      maxWidth: 400,
      width: "100%",
      maxHeight: "85vh",
      overflowY: "auto",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: theme.neoUI?.floating.card?.hoverShadow || "0 28px 70px -10px rgba(0, 0, 0, 0.18)",
      },
      // Scrollbar styling for overflow scenarios
      "&::-webkit-scrollbar": {
        width: 6,
      },
      "&::-webkit-scrollbar-thumb": {
        background: "rgba(0, 0, 0, 0.15)",
        borderRadius: 3,
      },
    },

    // Formulário
    form: {
      width: "100%",
      marginTop: theme.spacing(2),
    },

    // Botão com sombra verde
    submit: {
      ...theme.neoUI.buttons.action.root,
      margin: theme.spacing(3, 0, 2),
      borderRadius: 12,
      padding: "14px",
      fontSize: "1rem",
      height: "auto",
      minHeight: "auto",
      transition: "all 0.3s ease-in-out",
      "&:hover": {
        ...theme.neoUI.buttons.action.root["&:hover"],
      },
    },

    // Controles de idioma e tema (posicionados no topo)
    languageControl: {
      position: "absolute",
      top: 16,
      left: 16,
    },
    themeControl: {
      position: "absolute",
      top: 16,
      right: 16,
    },

    // Campos de entrada com sombra interna
    fieldRoot: {
      backgroundColor: "#ffffff",
      borderRadius: 10,
      boxShadow: theme.neoUI?.floating.input?.boxShadow || "inset 0 2px 4px rgba(0, 0, 0, 0.04)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0, 0, 0, 0.12)",
        borderWidth: 1,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
      "&.Mui-focused": {
        boxShadow: theme.neoUI?.floating.input?.focusShadow || "0 0 0 3px rgba(20, 164, 92, 0.15)",
      },
      "& input:-webkit-autofill": {
        WebkitTextFillColor: "#000 !important",
        WebkitBoxShadow: "0 0 0 1000px #fff inset !important",
      },
    },
    fieldInput: {
      color: "#000",
      padding: "14px 16px",
      "&::placeholder": { color: "#9EA3AA", opacity: 1 },
    },

    // Logo
    logo: {
      width: "60%",
      maxWidth: 180,
      marginBottom: theme.spacing(1),
    },

    // Título
    title: {
      color: "#333",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      fontWeight: 600,
    },
  };
});

const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
const RECOVERY_CODE_COOLDOWN_SECONDS = 60;

export const buildForgetPasswordPayload = (email) => ({
  email: (email || "").trim(),
});

export const buildResetPasswordPayload = (email, token, password) => ({
  email: (email || "").trim(),
  token: (token || "").trim(),
  password,
});

export const resolveRecoveryRetryAfterSeconds = (error, fallbackSeconds = RECOVERY_CODE_COOLDOWN_SECONDS) => {
  const responseRetryAfter = Number(
    error?.response?.headers?.["retry-after"] ||
    error?.response?.headers?.["Retry-After"]
  );
  const bodyRetryAfter = Number(error?.response?.data?.retryAfterSeconds);

  if (Number.isFinite(bodyRetryAfter) && bodyRetryAfter > 0) {
    return Math.ceil(bodyRetryAfter);
  }

  if (Number.isFinite(responseRetryAfter) && responseRetryAfter > 0) {
    return Math.ceil(responseRetryAfter);
  }

  return fallbackSeconds;
};

const ForgetPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isLight = theme.palette.type === "light";
  const colorMode = useContext(ColorModeContext);
  const history = useHistory();

  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showResetPasswordButton, setShowResetPasswordButton] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCooldownSeconds, setRecoveryCooldownSeconds] = useState(0);
  const [isSendingRecoveryCode, setIsSendingRecoveryCode] = useState(false);

  // Menu de idioma
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);

  const handleMenuLanguage = (event) => {
    setAnchorElLanguage(event.currentTarget);
    setMenuLanguageOpen(true);
  };

  const handleCloseMenuLanguage = () => {
    setAnchorElLanguage(null);
    setMenuLanguageOpen(false);
  };

  const handleToggleTheme = () => {
    if (colorMode?.toggleColorMode) {
      colorMode.toggleColorMode();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    if (recoveryCooldownSeconds <= 0) return undefined;

    const timer = setInterval(() => {
      setRecoveryCooldownSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [recoveryCooldownSeconds]);

  const handleSendEmail = async (values) => {
    if (recoveryCooldownSeconds > 0 || isSendingRecoveryCode) {
      return false;
    }

    const email = values.email;
    setIsSendingRecoveryCode(true);
    try {
      const response = await api.post("/forgetpassword", buildForgetPasswordPayload(email));

      if (response.status >= 200 && response.status < 300 && response.data?.success === true) {
        setRecoveryCooldownSeconds(RECOVERY_CODE_COOLDOWN_SECONDS);
        toast.success(i18n.t("resetPassword.toasts.emailSent"));
        setShowAdditionalFields(true);
        setShowResetPasswordButton(true);
        return true;
      }

      toast.error(response.data?.error || i18n.t("resetPassword.toasts.emailNotFound"));
      return false;
    } catch (err) {
      if (err?.response?.status === 429) {
        setRecoveryCooldownSeconds(resolveRecoveryRetryAfterSeconds(err));
      }
      toastError(err);
      return false;
    } finally {
      setIsSendingRecoveryCode(false);
    }
  };

  const handleResetPassword = async (values) => {
    const email = values.email;
    const token = values.token;
    const newPassword = values.newPassword;
    const confirmPassword = values.confirmPassword;

    if (newPassword === confirmPassword) {
      try {
        await api.post(
          "/resetpasswords",
          buildResetPasswordPayload(email, token, newPassword)
        );
        setError("");
        toast.success(i18n.t("resetPassword.toasts.passwordUpdated"));
        history.push("/login");
      } catch (err) {
        toastError(err);
      }
    }
  };

  const isResetPasswordButtonClicked = showResetPasswordButton;
  const UserSchema = Yup.object().shape({
    email: Yup.string().email(i18n.t("resetPassword.formErrors.email.invalid")).required(i18n.t("resetPassword.formErrors.email.required")),
    newPassword: isResetPasswordButtonClicked
      ? Yup.string()
        .required(i18n.t("resetPassword.formErrors.newPassword.required"))
        .matches(
          passwordRegex,
          i18n.t("resetPassword.formErrors.newPassword.matches")
        )
      : Yup.string(),
    confirmPassword: Yup.string().when("newPassword", {
      is: (newPassword) => isResetPasswordButtonClicked && newPassword,
      then: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], i18n.t("resetPassword.formErrors.confirmPassword.matches"))
        .required(i18n.t("resetPassword.formErrors.confirmPassword.required")),
      otherwise: Yup.string(),
    }),
  });

  return (
    <div className={classes.root}>
      {/* Seletor de idioma */}
      <div className={classes.languageControl}>
        <IconButton edge="start">
          <TranslateOutlined
            aria-label="language selector"
            aria-controls="menu-forget-language"
            aria-haspopup="true"
            onClick={handleMenuLanguage}
            style={{ color: "#FFFFFF" }}
          />
        </IconButton>
        <Menu
          id="menu-forget-language"
          anchorEl={anchorElLanguage}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={menuLanguageOpen}
          onClose={handleCloseMenuLanguage}
        >
          <MenuItem>
            <LanguageControl />
          </MenuItem>
        </Menu>
      </div>

      {/* Botão de tema */}
      <div className={classes.themeControl}>
        <IconButton edge="end" onClick={handleToggleTheme} aria-label="toggle theme">
          {isLight ? (
            <NightsStayOutlined style={{ color: "#FFFFFF" }} />
          ) : (
            <WbSunnyOutlined style={{ color: "#FFFFFF" }} />
          )}
        </IconButton>
      </div>

      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div className={classes.paper}>
          <img
            className={classes.logo}
            src={logo}
            alt="NeoZapp"
          />
          <Typography component="h1" variant="h5" className={classes.title}>
            {i18n.t("resetPassword.title")}
          </Typography>
          <Formik
            initialValues={{
              email: "",
              token: "",
              newPassword: "",
              confirmPassword: "",
            }}
            enableReinitialize={true}
            validationSchema={UserSchema}
            onSubmit={async (values, actions) => {
              try {
                if (showResetPasswordButton) {
                  await handleResetPassword(values);
                } else {
                  await handleSendEmail(values);
                }
              } finally {
                actions.setSubmitting(false);
              }
            }}
          >
            {({ touched, errors, isSubmitting, values }) => (
              <Form className={classes.form}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      id="email"
                      placeholder={i18n.t("resetPassword.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      autoComplete="email"
                      required
                      InputProps={{
                        notched: false,
                        classes: {
                          root: classes.fieldRoot,
                          input: classes.fieldInput,
                        },
                      }}
                    />
                  </Grid>
                  {showAdditionalFields && (
                    <>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          id="token"
                          placeholder={i18n.t("resetPassword.form.verificationCode")}
                          name="token"
                          error={touched.token && Boolean(errors.token)}
                          helperText={touched.token && errors.token}
                          autoComplete="off"
                          required
                          inputProps={{
                            maxLength: 6,
                            style: { textTransform: "uppercase", letterSpacing: "0.3em" },
                          }}
                          InputProps={{
                            notched: false,
                            classes: {
                              root: classes.fieldRoot,
                              input: classes.fieldInput,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          type={showPassword ? "text" : "password"}
                          id="newPassword"
                          placeholder={i18n.t("resetPassword.form.newPassword")}
                          name="newPassword"
                          error={touched.newPassword && Boolean(errors.newPassword)}
                          helperText={touched.newPassword && errors.newPassword}
                          autoComplete="off"
                          required
                          InputProps={{
                            notched: false,
                            classes: {
                              root: classes.fieldRoot,
                              input: classes.fieldInput,
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={togglePasswordVisibility}>
                                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          variant="outlined"
                          fullWidth
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          placeholder={i18n.t("resetPassword.form.confirmPassword")}
                          name="confirmPassword"
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          autoComplete="off"
                          required
                          InputProps={{
                            notched: false,
                            classes: {
                              root: classes.fieldRoot,
                              input: classes.fieldInput,
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton onClick={toggleConfirmPasswordVisibility}>
                                  {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                {showAdditionalFields && (
                  <Button
                    type="button"
                    fullWidth
                    variant="outlined"
                    color="primary"
                    className={classes.submit}
                    disabled={isSendingRecoveryCode || recoveryCooldownSeconds > 0}
                    onClick={async () => {
                      await handleSendEmail(values);
                    }}
                  >
                    {recoveryCooldownSeconds > 0
                      ? i18n.t("resetPassword.buttons.resendCooldown", { seconds: recoveryCooldownSeconds })
                      : i18n.t("resetPassword.buttons.resendCode")}
                  </Button>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  disabled={
                    isSubmitting ||
                    isSendingRecoveryCode ||
                    (!showResetPasswordButton && recoveryCooldownSeconds > 0)
                  }
                >
                  {showResetPasswordButton
                    ? i18n.t("resetPassword.buttons.submitPassword")
                    : recoveryCooldownSeconds > 0
                      ? i18n.t("resetPassword.buttons.resendCooldown", { seconds: recoveryCooldownSeconds })
                      : i18n.t("resetPassword.buttons.submitEmail")}
                </Button>
                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/login"
                    >
                      {i18n.t("resetPassword.buttons.back")}
                    </Link>
                  </Grid>
                </Grid>
                {error && (
                  <Typography variant="body2" color="error" style={{ marginTop: 16, textAlign: "center" }}>
                    {error}
                  </Typography>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </Container>
    </div>
  );
};

export default ForgetPassword;
